import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Loading, inr } from "@/components/common";
import PasswordGate from "@/components/PasswordGate";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ChevronRight, Building2, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value, sub, accent }) => (
  <div className={`bg-white border border-slate-200 rounded-xl p-4 ${accent ? "border-l-4 border-l-emerald-500" : ""}`}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`font-heading text-2xl font-extrabold mt-1 ${accent ? "text-emerald-600" : "text-slate-800"}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
  </div>
);

function CollegeRow({ c, open, onToggle, edits, setAmount }) {
  const configured = c.courses.filter((x) => Number(edits[x.id] ?? x.amount) > 0).length;
  return (
    <div className="border-b border-slate-100" data-testid={`cc-college-${c.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left" data-testid={`cc-toggle-${c.id}`}>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
        <Building2 className="w-4 h-4 text-[#4F46E5]" />
        <div className="flex-1 min-w-0"><div className="font-semibold text-slate-800 truncate">{c.name}</div><div className="text-[11px] text-slate-400">{c.city}</div></div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${configured === c.courses.length && configured ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{configured}/{c.courses.length} configured</span>
      </button>
      {open && (
        <div className="px-12 pb-3 space-y-1.5">
          {c.courses.map((co) => {
            const v = edits[co.id] ?? co.amount;
            const dirty = edits[co.id] !== undefined && Number(edits[co.id]) !== Number(co.amount);
            return (
              <div key={co.id} className="flex items-center gap-3 text-sm">
                <span className="flex-1 truncate text-slate-700">{co.name} <span className="text-[11px] text-slate-400">{co.level}</span></span>
                <div className="relative"><span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
                  <Input type="number" min="0" value={v || ""} placeholder="0" onChange={(e) => setAmount(co.id, e.target.value)} className={`w-36 h-8 pl-5 ${dirty ? "border-amber-400 bg-amber-50" : ""}`} data-testid={`cc-amount-${co.id}`} /></div>
              </div>
            );
          })}
          {!c.courses.length && <div className="text-xs text-slate-400">No courses</div>}
        </div>
      )}
    </div>
  );
}

function ConsultationEditor() {
  const [cols, setCols] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const load = () => { setCols(null); setEdits({}); api.get("/consultation-charges/editor").then(({ data }) => setCols(data)).catch(() => setCols([])); };
  useEffect(() => { load(); }, []);
  const shown = useMemo(() => {
    if (!cols) return [];
    const s = q.trim().toLowerCase();
    if (!s) return cols;
    return cols.map((c) => (c.name.toLowerCase().includes(s) ? c : { ...c, courses: c.courses.filter((x) => x.name.toLowerCase().includes(s)) })).filter((c) => c.name.toLowerCase().includes(s) || c.courses.length);
  }, [cols, q]);
  if (!cols) return <Loading label="Loading consultation charges" />;
  const all = cols.flatMap((c) => c.courses);
  const amts = all.map((x) => Number(edits[x.id] ?? x.amount) || 0).filter((x) => x > 0);
  const sum = amts.reduce((a, b) => a + b, 0);
  const changes = Object.entries(edits).filter(([id, v]) => Number(v) !== Number(all.find((x) => x.id === id)?.amount)).map(([course_id, amount]) => ({ course_id, amount: Number(amount) || 0 }));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/consultation-charges/editor", { changes }); toast.success(`${data.updated} consultation charges saved`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const setAll = (v) => setOpen(v ? Object.fromEntries(shown.map((c) => [c.id, true])) : {});
  return (
    <div>
      <PageHeader title="Consultation Charges" subtitle="Set consultation charges (revenue) per admission for each course of every featured college. Only Admin can update." testid="cc-header">
        <Button onClick={save} disabled={!changes.length || saving} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="cc-save-changes"><Save className="w-4 h-4 mr-1" /> Save Changes{changes.length ? ` (${changes.length})` : ""}</Button>
      </PageHeader>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="cc-stats">
        <Stat label="Featured Colleges" value={cols.length} />
        <Stat label="Total Courses" value={all.length} />
        <Stat label="Configured" value={amts.length} sub={`${all.length ? Math.round((amts.length * 100) / all.length) : 0}% of courses`} accent />
        <Stat label="Total Potential per Admission" value={inr(amts.length ? Math.round(sum / amts.length) : 0)} sub={`Avg of ${amts.length} configured · Sum ${inr(sum)}`} />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-3 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search college or course..." className="pl-9" data-testid="cc-search" /></div>
        <Button variant="outline" onClick={() => setAll(true)} data-testid="cc-expand-all">Expand all</Button>
        <Button variant="outline" onClick={() => setAll(false)} data-testid="cc-collapse-all">Collapse all</Button>
        <Button variant="outline" onClick={load} data-testid="cc-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="cc-college-list">
        {shown.map((c) => <CollegeRow key={c.id} c={c} open={!!open[c.id] || (!!q.trim() && c.courses.length < 40)} onToggle={() => setOpen({ ...open, [c.id]: !open[c.id] })} edits={edits} setAmount={(id, v) => setEdits((e) => ({ ...e, [id]: v }))} />)}
        {!shown.length && <div className="p-6 text-center text-sm text-slate-400">No matches</div>}
      </div>
    </div>
  );
}

export default function ConsultationCharges() {
  return <PasswordGate gate="consultation" title="Consultation Charges"><ConsultationEditor /></PasswordGate>;
}
